import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { isSchemaRefusal } from "../flatbuffers/api/flatBufferBaseQuery";

/**
 * What RTK Query's `retry` hands a retry condition besides the error: the
 * attempt that just failed (1 for the first send), and the endpoint's
 * `extraOptions`. Under `withRetries` that is always an object (its per-request
 * copy); a `retry` used directly leaves it undefined when the endpoint declares
 * none.
 */
interface RetryAttempt {
  attempt: number;
  extraOptions?: { maxRetries?: number };
}

/**
 * The retry budget for one request: the endpoint's own
 * `extraOptions.maxRetries` when it sets one, else the API's.
 *
 * `retry` cannot take both a `maxRetries` and a `retryCondition`, and once it
 * has a condition it stops reading `maxRetries` itself, so a condition that
 * ignored the endpoint's value would silently retire RTK's per-endpoint knob.
 */
function retryBudget(maxRetries: number, { extraOptions }: RetryAttempt): number {
  return extraOptions?.maxRetries ?? maxRetries;
}

/**
 * fetchBaseQuery reports a body read that never completed (the connection
 * dropped mid-body, the request was aborted) as a PARSING_ERROR on the reply's
 * own status, just like a body that arrived and did not decode. Only the
 * error text, `String()` of what the read rejected with, tells them apart.
 */
const INCOMPLETE_READ = /^(TypeError|AbortError)\b/;

/**
 * True for a 2xx reply whose body arrived and could not, or must not, be read.
 *
 * On the FlatBuffer base query that is an LN2E envelope whose schema hash has
 * no handler registered, say, or a schema refusal (`isSchemaRefusal`: the
 * server's schema changed the body's type in a way this app cannot read,
 * returned as a CUSTOM_ERROR). The server has already answered and would send
 * the same bytes again. Not unreadable, and still retryable:
 * - a PARSING_ERROR on any other status (a proxy's HTML page on a 502, read by
 *   a plain `fetchBaseQuery`), which is the failure itself, not the answer;
 * - a 2xx whose body read was cut short (`INCOMPLETE_READ`): the answer never
 *   arrived, and a resend can fix a dropped connection. (An aborted read is
 *   not fixed by one: RTK 1.9.7's retry reuses the aborted signal, so each
 *   retry fails at once and costs only its backoff.) A decoder that fails
 *   with a TypeError of its own is not mistaken for one: the FlatBuffer base
 *   query rethrows every decode failure as an `Error` carrying its
 *   `[FlatBuffer]` message. A custom `responseHandler` must do the same.
 */
export function isUnreadableReply(error: FetchBaseQueryError): boolean {
  if (isSchemaRefusal(error)) {
    return true;
  }
  return (
    error.status === "PARSING_ERROR" &&
    error.originalStatus >= 200 &&
    error.originalStatus < 300 &&
    !INCOMPLETE_READ.test(error.error)
  );
}

/**
 * Builds an RTK Query `retry` condition that does not retry an answer the
 * client could not read (see `isUnreadableReply`).
 *
 * Retrying one only repeats the request (and, for a mutation, its effect) and
 * shows the error after the backoff instead of at once. In RTK 1.9.7 the
 * retries also ignore the abort signal, so they outlive the component that
 * asked. Every other error is still retried up to `maxRetries`.
 *
 * Every API gets this through `withRetries` / `createRetryingBaseQuery`
 * (retryingBaseQuery.ts); build an API's base query with those rather than
 * calling `retry` directly.
 */
export function retryUnlessUnreadable(maxRetries: number) {
  return (error: FetchBaseQueryError, _args: unknown, attempt: RetryAttempt): boolean =>
    !isUnreadableReply(error) && attempt.attempt <= retryBudget(maxRetries, attempt);
}

/**
 * Builds an RTK Query `retry` condition that does not retry client errors.
 *
 * A 4xx answer is the same on every attempt — a 403 for a role the endpoint
 * refuses, a 401 for a wrong one-time code — so retrying it only delays the
 * message by the backoff (and, for a code, spends attempts against the
 * server's limiter). 408 and 429 are transient and are still retried, as is
 * every other error, up to `maxRetries`.
 *
 * Pass the result as an endpoint's `extraOptions.retryCondition`, or pass the
 * function itself as the `rule` of `withRetries` / `createRetryingBaseQuery`
 * for a whole API. An endpoint's condition replaces the API's rather than
 * adding to it; `withRetries` re-applies the unreadable-reply rule to any such
 * condition, and this one also carries the rule itself, as
 * `retryUnlessUnreadable` does, for a `retry` it is used with directly.
 */
export function retryUnlessClientError(maxRetries: number) {
  const unlessUnreadable = retryUnlessUnreadable(maxRetries);
  return (error: FetchBaseQueryError, args: unknown, attempt: RetryAttempt): boolean => {
    const { status } = error;
    const transient = status === 408 || status === 429;
    if (typeof status === "number" && status >= 400 && status < 500 && !transient) {
      return false;
    }
    return unlessUnreadable(error, args, attempt);
  };
}

export default retryUnlessClientError;
